import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import type { addExtraOptionPanel_ListQuery } from './__generated__/addExtraOptionPanel_ListQuery.graphql';
import {
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    PanelItem,
    ItemDot,
    ItemName,
    ItemActionSuccess,
    PanelItemSpaced,
    ItemNameAction,
    EmptyState
} from '../common/styled';

export interface AddExtraOptionPanelProps {
    productId: string;
    productModelId: number;
    existingOptionIds: string[];
    onBack: () => void;
    onCreateNew: () => void;
    onAssigned: () => void;
}

const AddExtraOptionPanelContent: FC<AddExtraOptionPanelProps> = ({ productId, productModelId, existingOptionIds, onBack, onCreateNew, onAssigned }) => {
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addExtraOptionPanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addExtraOptionPanel_ListQuery>(
        graphql`
            query addExtraOptionPanel_ListQuery {
                myAdditionalAttributes(status: 0) {
                    id
                    additionalAttributeID
                    name
                    products {
                        productID
                    }
                }
            }
        `,
        {}
    );

    const allOptions = React.useMemo(() => {
        return (data?.myAdditionalAttributes || []).map((attr) => ({
            id: String(attr.additionalAttributeID),
            globalId: attr.id,
            name: attr.name || `Option Set #${attr.additionalAttributeID}`,
            productIDs: (attr.products || []).filter((p): p is NonNullable<typeof p> => p != null).map((p) => p.productID)
        }));
    }, [data]);

    const [assigning, setAssigning] = useState<string | null>(null);

    const [commitSave] = useMutation(graphql`
        mutation addExtraOptionPanel_AssignMutation($input: SaveAdditionalAttributeInput!) {
            saveAdditionalAttribute(input: $input) {
                id
                additionalAttributeID
            }
        }
    `);

    const handleAssign = (option: { globalId: string; productIDs: string[] }) => {
        setAssigning(option.globalId);
        commitSave({
            variables: {
                input: {
                    additionalAttributeID: option.globalId,
                    status: 0,
                    productIDs: [...option.productIDs, String(productModelId)]
                }
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableOptions = allOptions.filter((opt) => !existingOptionIds.includes(opt.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add Extra Options', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableOptions.length === 0 && <EmptyState>{T._('All option sets are already assigned', 'Admin')}</EmptyState>}
                {availableOptions.map((opt) => (
                    <PanelItem key={opt.globalId} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(opt)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{opt.name}</ItemName>
                        <ItemActionSuccess>{assigning === opt.globalId ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced $clickable onClick={() => onCreateNew()}>
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddExtraOptionPanel: FC<AddExtraOptionPanelProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add Extra Options', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddExtraOptionPanelContent {...props} />
        </Suspense>
    );
};
